import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { supabase } from "../cloudflareClient";
import Header from "../components/Header";
import {
  AiOutlineTeam,
  AiOutlineFileText,
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSearch,
  AiOutlineClose,
  AiOutlineEye,
  AiOutlineSafety,
  AiOutlineMail,
  AiOutlineLock,
  AiOutlinePhone,
  AiOutlineIdcard
} from "react-icons/ai";
import "./Admin.css";

function Admin() {
  const history = useHistory();

  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Data
  const [users, setUsers] = useState([]);
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFacturesModal, setShowFacturesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states for Create Account
  const [newUserForm, setNewUserForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    address: "",
    cnie: "",
    ice: "",
    if: "",
    tp: "",
    role: "user"
  });

  // Form states for Edit User
  const [editUserForm, setEditUserForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    address: "",
    cnie: "",
    ice: "",
    if: "",
    tp: "",
    photo_url: "",
    role: "user"
  });
  const [photoFile, setPhotoFile] = useState(null);

  // Action status
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  // Check Auth & Admin Status
  useEffect(() => {
    const checkAdminAuth = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          history.push("/");
          return;
        }

        const user = session.user;
        setCurrentUser(user);

        // Check if user is admin
        const { data: entrepreneurData } = await supabase
          .from("entrepreneur")
          .select("*")
          .eq("user_id", user.id)
          .single();

        const userEmail = (user.email || "").toLowerCase();
        const hasAdminRole = user.user_metadata?.role === "admin" || entrepreneurData?.role === "admin" || entrepreneurData?.is_admin === true;
        const hasAdminEmail = userEmail.includes("admin") || user.user_metadata?.role === "admin";

        // Grant admin access if role is admin or admin email
        if (hasAdminRole || hasAdminEmail) {
          setIsAdmin(true);
          await loadDashboardData();
        } else {
          // If first user or needs admin access, let them know
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("Erreur vérification admin:", err);
      } finally {
        setLoading(false);
      }
    };

    checkAdminAuth();
  }, [history]);

  // Load all platform data
  const loadDashboardData = async () => {
    try {
      // 1. Fetch all entrepreneurs
      const { data: usersData, error: usersErr } = await supabase
        .from("entrepreneur")
        .select("*")
        .order("id", { ascending: false });

      if (usersErr) throw usersErr;

      // 2. Fetch all factures
      const { data: facturesData, error: facturesErr } = await supabase
        .from("factures")
        .select("*")
        .order("id", { ascending: false });

      if (facturesErr) throw facturesErr;

      // 3. Fetch all clients
      const { data: clientsData } = await supabase
        .from("clients")
        .select("*");

      setUsers(usersData || []);
      setFactures(facturesData || []);
      setClients(clientsData || []);
    } catch (err) {
      console.error("Erreur chargement données dashboard:", err.message);
    }
  };

  // KPIs calculations
  const totalUsers = users.length;
  const totalFacturesCount = factures.length;
  const totalClientsCount = clients.length;

  // Filtered Users list
  const filteredUsers = users.filter((u) => {
    const q = searchTerm.toLowerCase();
    return (
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.phone && u.phone.toLowerCase().includes(q)) ||
      (u.ice && u.ice.toLowerCase().includes(q)) ||
      (u.cnie && u.cnie.toLowerCase().includes(q)) ||
      (u.address && u.address.toLowerCase().includes(q))
    );
  });

  // Handle Create User Account
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    setActionError("");

    try {
      if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
        throw new Error("L'email, le mot de passe et le nom sont obligatoires.");
      }

      // 1. Sign up new user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUserForm.email,
        password: newUserForm.password,
        options: {
          data: {
            name: newUserForm.name,
            role: newUserForm.role || "user"
          }
        }
      });

      if (authError) throw authError;

      const createdUserId = authData.user?.id;

      // 2. Insert entrepreneur profile in table
      const { error: profileError } = await supabase
        .from("entrepreneur")
        .insert([
          {
            user_id: createdUserId,
            name: newUserForm.name,
            phone: newUserForm.phone,
            address: newUserForm.address,
            cnie: newUserForm.cnie,
            ice: newUserForm.ice,
            if: newUserForm.if,
            tp: newUserForm.tp,
            role: newUserForm.role || "user"
          }
        ]);

      if (profileError) throw profileError;

      alert("Nouveau compte utilisateur créé avec succès !");
      setShowCreateModal(false);
      setNewUserForm({
        email: "",
        password: "",
        name: "",
        phone: "",
        address: "",
        cnie: "",
        ice: "",
        if: "",
        tp: "",
        role: "user"
      });
      await loadDashboardData();
    } catch (err) {
      setActionError(err.message || "Erreur lors de la création du compte.");
    } finally {
      setSaving(false);
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      email: user.email || "",
      password: "",
      name: user.name || "",
      phone: user.phone || "",
      address: user.address || "",
      cnie: user.cnie || "",
      ice: user.ice || "",
      if: user.if || "",
      tp: user.tp || "",
      photo_url: user.photo_url || "",
      role: user.role || "user"
    });
    setPhotoFile(null);
    setActionError("");
    setShowEditModal(true);
  };

  // Save Edit User
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSaving(true);
    setActionError("");

    try {
      const trimmedEmail = editUserForm.email.trim();
      const trimmedPassword = editUserForm.password.trim();

      const { error: updateAuthError } = await supabase.auth.updateUser({
        user_id: selectedUser.user_id || selectedUser.id,
        ...(trimmedEmail && trimmedEmail !== (selectedUser.email || "") ? { email: trimmedEmail } : {}),
        ...(trimmedPassword ? { password: trimmedPassword } : {})
      });

      if (updateAuthError) throw updateAuthError;

      let photoUrl = editUserForm.photo_url || selectedUser.photo_url || "";
      if (photoFile) {
        const extension = photoFile.name.split(".").pop()?.toLowerCase() || "jpg";
        const photoPath = `entrepreneur-photos/${selectedUser.user_id || selectedUser.id}-${Date.now()}.${extension}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("factures-pdf")
          .upload(photoPath, photoFile);
        if (uploadError) throw uploadError;
        photoUrl = supabase.storage.from("factures-pdf").getPublicUrl(uploadData.path).data.publicUrl;
      }

      const { error } = await supabase
        .from("entrepreneur")
        .update({
          email: trimmedEmail || selectedUser.email,
          name: editUserForm.name,
          phone: editUserForm.phone,
          address: editUserForm.address,
          cnie: editUserForm.cnie,
          ice: editUserForm.ice,
          if: editUserForm.if,
          tp: editUserForm.tp,
          photo_url: photoUrl,
          role: editUserForm.role
        })
        .eq("id", selectedUser.id);

      if (error) throw error;

      alert("Informations mises à jour avec succès !");
      setShowEditModal(false);
      await loadDashboardData();
    } catch (err) {
      setActionError(err.message || "Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  // Delete User
  const handleDeleteUser = async (user) => {
    const confirmDelete = window.confirm(
      `Êtes-vous certain de vouloir supprimer le compte et les données de "${user.name}" ? Cette action est irréversible.`
    );
    if (!confirmDelete) return;

    try {
      // 1. Delete associated factures
      await supabase.from("factures").delete().eq("entrepreneur_id", user.id);
      // 2. Delete associated clients
      await supabase.from("clients").delete().eq("entrepreneur_id", user.id);
      // 3. Delete entrepreneur profile
      const { error } = await supabase.from("entrepreneur").delete().eq("id", user.id);
      if (error) throw error;

      alert("Utilisateur et données associées supprimés.");
      await loadDashboardData();
    } catch (err) {
      alert("Erreur lors de la suppression : " + err.message);
    }
  };

  // Open Factures of User
  const handleViewUserFactures = (user) => {
    setSelectedUser(user);
    setShowFacturesModal(true);
  };

  // Get user's factures
  const userFactures = selectedUser
    ? factures.filter((f) => f.entrepreneur_id === selectedUser.id)
    : [];

  if (loading) {
    return (
      <div className="admin-page">
        <Header activePage="admin" />
        <div className="admin-loading">
          <div className="spinner" />
          <p>Chargement de l'espace administration...</p>
        </div>
      </div>
    );
  }

  // Access denied screen if not admin
  if (!isAdmin) {
    return (
      <div className="admin-page">
        <Header activePage="admin" />
        <div className="access-denied-card">
          <AiOutlineSafety className="denied-icon" />
          <h2>Accès Administrateur Restreint</h2>
          <p>
            Votre compte (<strong>{currentUser?.email}</strong>) ne dispose pas des privilèges
            administrateur nécessaires pour gérer les comptes.
          </p>
          <div className="denied-actions">
            <button
              type="button"
              className="btn-admin-primary"
              onClick={() => history.push("/facteur-editeur")}
            >
              Retour à l'éditeur de facture
            </button>
            <button
              type="button"
              className="btn-admin-secondary"
              onClick={async () => {
                // Easy self-promote for initial setup if user confirms
                if (window.confirm("Activer les droits administrateur pour votre compte actuel ?")) {
                  try {
                    const { error } = await supabase.auth.promote();
                    if (error) throw error;
                    setIsAdmin(true);
                    await loadDashboardData();
                  } catch (e) {
                    alert("Erreur: " + e.message);
                  }
                }
              }}
            >
              Définir ce compte comme Administrateur
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <Header activePage="admin" />

      {/* Admin Title Header */}
      <div className="admin-header-bar">
        <div>
          <div className="admin-badge">
            <AiOutlineSafety />
            <span>Panneau d'Administration</span>
          </div>
          <h1 className="admin-title">Gestion des Utilisateurs & Comptes</h1>
          <p className="admin-subtitle">
            Supervisez les auto-entrepreneurs, gérez leurs profils légaux et suivez l'activité de la plateforme.
          </p>
        </div>

        <button
          type="button"
          className="btn-admin-primary"
          onClick={() => {
            setActionError("");
            setShowCreateModal(true);
          }}
        >
          <AiOutlinePlus />
          <span>Créer un Compte</span>
        </button>
      </div>

      {/* KPI Stats Grid */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-blue">
            <AiOutlineTeam />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Auto-Entrepreneurs</span>
            <span className="kpi-value">{totalUsers}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-indigo">
            <AiOutlineFileText />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total Factures</span>
            <span className="kpi-value">{totalFacturesCount}</span>
          </div>
        </div>

        {/* <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-emerald">
            <AiOutlineDollar />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Chiffre d'Affaires Global</span>
            <span className="kpi-value">{totalGlobalRevenue.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD</span>
          </div>
        </div> */}

        <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-purple">
            <AiOutlineTeam />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total Clients</span>
            <span className="kpi-value">{totalClientsCount}</span>
          </div>
        </div>
      </div>

      {/* Users Management Section */}
      <div className="admin-section-card">
        <div className="section-toolbar">
          <div className="search-box">
            <AiOutlineSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher par nom, téléphone, CNIE, ICE..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={() => setSearchTerm("")}
              >
                <AiOutlineClose />
              </button>
            )}
          </div>

          <div className="table-count-badge">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? "s" : ""}
          </div>
        </div>

        {/* Users Table */}
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Auto-Entrepreneur</th>
                <th>Contact</th>
                <th>Identifiants Légaux</th>
                <th>Adresse</th>
                <th>Factures</th>
                <th>Rôle</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-table-cell">
                    Aucun utilisateur trouvé pour cette recherche.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const countUserFactures = factures.filter(
                    (f) => f.entrepreneur_id === u.id
                  ).length;

                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {(u.name || "U").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="user-names">
                            <span className="user-primary-name">{u.name || "Sans nom"}</span>
                            <span className="user-sub-id">ID: #{u.id}</span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="contact-cell">
                          <span>{u.phone || "—"}</span>
                          <span className="user-email">{u.email || "—"}</span>
                        </div>
                      </td>

                      <td>
                        <div className="legal-cell">
                          <span><strong>CNIE :</strong> {u.cnie || "—"}</span>
                          <span><strong>ICE :</strong> {u.ice || "—"}</span>
                          {u.if && <span><strong>IF :</strong> {u.if}</span>}
                          {u.tp && <span><strong>TP :</strong> {u.tp}</span>}
                        </div>
                      </td>

                      <td>
                        <span className="address-text">{u.address || "—"}</span>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="factures-count-btn"
                          onClick={() => handleViewUserFactures(u)}
                        
                        >
                          
                          <span>{countUserFactures} facture{countUserFactures > 1 ? "s" : ""}</span>
                        </button>
                      </td>

                      <td>
                        <span className={`role-badge ${u.role === "admin" ? "role-admin" : "role-user"}`}>
                          {u.role === "admin" ? "Administrateur" : "Utilisateur"}
                        </span>
                      </td>

                      <td>
                        <div className="action-buttons-group">
                          <button
                            type="button"
                            className="btn-table-edit"
                            title="Modifier les informations"
                            onClick={() => handleOpenEdit(u)}
                          >
                            <AiOutlineEdit />
                          </button>
                          <button
                            type="button"
                            className="btn-table-view"
                            title="Voir les factures"
                            onClick={() => handleViewUserFactures(u)}
                          >
                            <AiOutlineEye />
                          </button>
                          <button
                            type="button"
                            className="btn-table-delete"
                            title="Supprimer cet utilisateur"
                            onClick={() => handleDeleteUser(u)}
                          >
                            <AiOutlineDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =========================================================
          MODAL: CRÉER UN NOUVEAU COMPTE UTILISATEUR
          ========================================================= */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <AiOutlinePlus className="modal-icon" />
                <span>Créer un Nouveau Compte Auto-Entrepreneur</span>
              </div>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setShowCreateModal(false)}
              >
                <AiOutlineClose />
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {actionError && (
                  <div className="modal-error-alert">{actionError}</div>
                )}

                <div className="modal-form-grid">
                  <div className="input-group">
                    <label>
                      <AiOutlineMail />
                      <span>Adresse Email de Connexion *</span>
                    </label>
                    <input
                      className="effect-1"
                      type="email"
                      required
                      placeholder="utilisateur@exemple.com"
                      value={newUserForm.email}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, email: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>
                      <AiOutlineLock />
                      <span>Mot de passe temporaire *</span>
                    </label>
                    <input
                      className="effect-1"
                      type="password"
                      required
                      placeholder="Minimum 6 caractères"
                      value={newUserForm.password}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, password: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>
                      <AiOutlineIdcard />
                      <span>Nom et Prénom *</span>
                    </label>
                    <input
                      className="effect-1"
                      type="text"
                      required
                      placeholder="Ex: Mohammed Alami"
                      value={newUserForm.name}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, name: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>
                      <AiOutlinePhone />
                      <span>Téléphone</span>
                    </label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="06 12 34 56 78"
                      value={newUserForm.phone}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, phone: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>CNIE</label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="Ex: AB123456"
                      value={newUserForm.cnie}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, cnie: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Numéro I.C.E</label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="Ex: 001234567000089"
                      value={newUserForm.ice}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, ice: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Identifiant Fiscal (IF)</label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="Ex: 12345678"
                      value={newUserForm.if}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, if: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Taxe Professionnelle (TP)</label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="Ex: 87654321"
                      value={newUserForm.tp}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, tp: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group full-width">
                    <label htmlFor="entrepreneur-photo">Photo de la carte</label>
                    <input
                      className="effect-1"
                      id="entrepreneur-photo"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                    />
                    <small className="file-help-text">
                      {photoFile ? photoFile.name : editUserForm.photo_url ? "Une photo est déjà enregistrée." : "Aucune photo enregistrée."}
                    </small>
                  </div>

                  <div className="input-group full-width">
                    <label>Adresse Professionnelle</label>
                    <input
                      className="effect-1"
                      type="text"
                      placeholder="Adresse complète"
                      value={newUserForm.address}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, address: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Rôle du compte</label>
                    <select
                      className="effect-1"
                      value={newUserForm.role}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, role: e.target.value })
                      }
                    >
                      <option value="user">Utilisateur standard</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-modal-submit"
                  disabled={saving}
                >
                  {saving ? "Création en cours..." : "Créer le compte"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL: MODIFIER UN UTILISATEUR
          ========================================================= */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <AiOutlineEdit className="modal-icon" />
                <span>Modifier : {selectedUser.name}</span>
              </div>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setShowEditModal(false)}
              >
                <AiOutlineClose />
              </button>
            </div>

            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                {actionError && (
                  <div className="modal-error-alert">{actionError}</div>
                )}

                <div className="modal-form-grid">
                    <div className="input-group">
                      <label>
                        <AiOutlineMail />
                        <span>Email du compte</span>
                      </label>
                      <input
                        className="effect-1"
                        type="email"
                        required
                        value={editUserForm.email}
                        onChange={(e) =>
                          setEditUserForm({ ...editUserForm, email: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>
                        <AiOutlineLock />
                        <span>Nouveau mot de passe</span>
                      </label>
                      <input
                        className="effect-1"
                        type="password"
                        placeholder="Laisser vide pour ne pas modifier"
                        value={editUserForm.password}
                        onChange={(e) =>
                          setEditUserForm({ ...editUserForm, password: e.target.value })
                        }
                      />
                    </div>

                  <div className="input-group">
                    <label>Nom et Prénom *</label>
                    <input
                      className="effect-1"
                      type="text"
                      required
                      value={editUserForm.name}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, name: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Téléphone</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.phone}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, phone: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>CNIE</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.cnie}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, cnie: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>I.C.E</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.ice}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, ice: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Identifiant Fiscal (IF)</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.if}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, if: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Taxe Professionnelle (TP)</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.tp}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, tp: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group full-width">
                    <label>Adresse Professionnelle</label>
                    <input
                      className="effect-1"
                      type="text"
                      value={editUserForm.address}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, address: e.target.value })
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Rôle</label>
                    <select
                      className="effect-1"
                      value={editUserForm.role}
                      onChange={(e) =>
                        setEditUserForm({ ...editUserForm, role: e.target.value })
                      }
                    >
                      <option value="user">Utilisateur standard</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-modal-submit"
                  disabled={saving}
                >
                  {saving ? "Sauvegarde..." : "Enregistrer les modifications"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL: FACTURES DE L'UTILISATEUR SÉLECTIONNÉ
          ========================================================= */}
      {showFacturesModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowFacturesModal(false)}>
          <div className="modal-card modal-card-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <AiOutlineFileText className="modal-icon" />
                <span>Factures émises par : {selectedUser.name}</span>
              </div>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setShowFacturesModal(false)}
              >
                <AiOutlineClose />
              </button>
            </div>

            <div className="modal-body">
              {userFactures.length === 0 ? (
                <div className="empty-factures-view">
                  <AiOutlineFileText className="empty-icon" />
                  <p>Aucune facture émise par cet utilisateur pour le moment.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>N° Facture</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Montant Total</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userFactures.map((f, idx) => (
                        <tr key={idx}>
                          <td>{f.facture_number}</td>
                          <td>{f.date || "—"}</td>
                          <td>{f.client || "—"}</td>
                          <td>
                           
                              {Number(f.total_ht || 0).toFixed(2)} MAD
                           
                          </td>
                          <td>
                            {f.pdf_url ? (
                              <button
                                type="button"
                                className="btn-view-pdf"
                                onClick={() => window.open(f.pdf_url, "_blank")}
                              >
                                <span>PDF</span>
                                <AiOutlineEye  font-size="1.25rem"/>
                               
                              </button>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Pas de PDF</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-modal-cancel"
                onClick={() => setShowFacturesModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
